"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

const SLIDE_INTERVAL_MS = 3000;

export interface HeroSlide {
  label: string;
  content: ReactNode;
}

/**
 * Autoplaying hero mockup carousel.
 * Slide markup is server-rendered and passed in, so only the active-index
 * bookkeeping runs on the client.
 */
export function PillarHeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const slideCount = slides.length;

  useEffect(() => {
    carouselTimer.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slideCount);
    }, SLIDE_INTERVAL_MS);
    return () => {
      if (carouselTimer.current) clearInterval(carouselTimer.current);
    };
  }, [slideCount]);

  const goToSlide = useCallback(
    (idx: number) => {
      if (carouselTimer.current) clearInterval(carouselTimer.current);
      setActiveSlide(idx);
      carouselTimer.current = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % slideCount);
      }, SLIDE_INTERVAL_MS);
    },
    [slideCount]
  );

  return (
    <div className="iphone-wrap">
      <div className="slide-label">{slides[activeSlide].label}</div>
      <div className="screen-carousel">
        {slides.map((slide, idx) => (
          <div
            key={slide.label}
            className={`screen-slide ${idx === activeSlide ? "active" : ""}`}
            data-label={slide.label}
          >
            {slide.content}
          </div>
        ))}
      </div>
      <div className="carousel-dots">
        {slides.map((slide, idx) => (
          <button
            key={slide.label}
            className={`cdot ${idx === activeSlide ? "active" : ""}`}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
